import { useState, useEffect } from "react"
import { View, Text, TextInput, TouchableOpacity, StyleSheet, ScrollView, Image, Platform, ActivityIndicator } from "react-native"
import { Picker } from "@react-native-picker/picker"
import ImageUploader from "react-images-upload"
// On web this resolves to the mock in src/mocks (package.json "browser" field);
// on native, metro.config.js points it back at the real package.
import { launchImageLibrary } from "react-native-image-picker"
import TopGreenHeader from "../Common/TopGreenHeader"
import Polygon from "../.././assets/Polygon.png"
import { useNavigation } from "@react-navigation/native"
import { createKidCreator, resetCreateKidSuccessCreator } from "../../slices/authSlice"
import { useDispatch, useSelector } from "react-redux"
import { ToastContainer, toast } from "react-toastify"
import AsyncStorage from "@react-native-async-storage/async-storage"
import API_PATHS from "../../apiCalls/ApiPath"

const EMPTY_FORM = {
  name: "",
  age: "",
  gender: "",
  emergencyNumber: "",
  allergy: "",
  healthIssue: "",
  profilePic: null,
}

const AddKidScreen = ({ navigation, route }) => {
  const [form, setForm] = useState(EMPTY_FORM)
  const dispatch = useDispatch()
  const createKidSuccess = useSelector((state) => state.auth.createKidSuccess)
  const createKidFailure = useSelector((state) => state.auth.createKidFailure)
  const createKid = useSelector((state) => state.auth.createKid)
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (createKidSuccess) {
      setLoading(false);
      toast.success(createKid?.Message || "Kid created successfully");
      setForm(EMPTY_FORM);
      // Reset the flag so re-entering the screen doesn't auto-navigate.
      dispatch(resetCreateKidSuccessCreator());
      navigation.navigate(route?.params?.returnTo || 'SelectKidScreen')
    }
  }, [createKidSuccess, navigation, route, createKid, dispatch])

  useEffect(() => {
    if (createKidFailure) {
      setLoading(false);
      toast.error(typeof createKidFailure === 'string' ? createKidFailure : "Failed to create kid. Please try again.");
      dispatch(resetCreateKidSuccessCreator());
    }
  }, [createKidFailure, dispatch])

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value })
  }

  const dataURItoBlob = (dataURI) => {
    let byteString;
    if (dataURI.split(',')[0].indexOf('base64') >= 0) {
      byteString = atob(dataURI.split(',')[1]);
    } else {
      byteString = unescape(dataURI.split(',')[1]);
    }

    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

    const ia = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    return new Blob([ia], { type: mimeString });
  }

  const uploadImage = async (dataURI) => {
    const formData = new FormData();

    try {
      if (Platform.OS === 'web') {
        const blob = dataURItoBlob(dataURI);
        formData.append('file', blob, 'profile.jpg');
      } else {
        const fileInfo = {
          uri: dataURI,
          type: 'image/jpeg',
          name: 'profile.jpg',
        };
        formData.append('file', fileInfo);
      }

      const token = await AsyncStorage.getItem("token");
      const clientToken = await AsyncStorage.getItem("clientToken");
      if (!token) {
        console.error("No token found")
        toast.error("Authentication failed. Please log in again.")
        return null
      }

      console.log("Sending form data:", formData);

      const response = await fetch(`${API_PATHS.BASE_URL}${API_PATHS.UPLOAD_FILE}`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${clientToken || token}`,
        },
      })

      console.log("Response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text()
        console.error("Server response:", errorText)
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      console.log("Upload result:", result);

      if (result.Success === 1) {
        return result.Data.url
      } else {
        throw new Error(result.Message || "Failed to upload image")
      }
    } catch (error) {
      console.error("Error uploading image:", error)
      toast.error(`Failed to upload image: ${error.message}`)
      return null
    }
  }

  const handleSubmit = async () => {
    if (!form.name || !form.age || !form.gender || !form.emergencyNumber) {
      toast.error(`Please fill in all required fields.`)
      return
    }

    if (!form.profilePic) {
      toast.error("Please upload a profile picture.")
      return
    }

    try {
      setLoading(true);
      let imageUrl = form.profilePic;
      if (
        typeof form.profilePic === 'string' &&
        (form.profilePic.startsWith('data:') ||
          form.profilePic.startsWith('file:') ||
          form.profilePic.startsWith('content:'))
      ) {
        console.log("Uploading image data URI");
        imageUrl = await uploadImage(form.profilePic);
        if (!imageUrl) {
          toast.error("Failed to upload image. Please try again.");
          return;
        }
      }

      const obj = {
        name: form.name,
        gender: form.gender,
        emergencyPhone: form.emergencyNumber,
        healthIssue: form.healthIssue || "",
        allergy: form.allergy || "",
        profilePic: imageUrl,
        age: form.age,
      }

      console.log("Dispatching kid creation with data:", obj);
      dispatch(createKidCreator({ obj }));


    } catch (error) {
      console.error("Error in form submission:", error);
      toast.error(`Error submitting form: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }

  const onDrop = async (pictureFiles, pictureDataURLs) => {
    if (pictureDataURLs && pictureDataURLs.length > 0) {
      console.log("Image selected:", pictureDataURLs[0].substring(0, 50) + "...");
      setForm({ ...form, profilePic: pictureDataURLs[0] });
    }
  }

  const pickImage = () => {
    launchImageLibrary({ mediaType: "photo", quality: 0.8 }, (response) => {
      if (!response.didCancel && !response.errorCode && response.assets?.[0]) {
        handleChange("profilePic", response.assets[0].uri)
      }
    })
  }

  return (
    <>
      <TopGreenHeader Heading={"Add Kid"} navigation={navigation} isBackIcon={true} isNotification={false} />
      <ScrollView contentContainerStyle={styles.container}>
        <ToastContainer />
        {Platform.OS === 'web' ? (
          <ImageUploader
            withIcon={false}
            buttonText="Upload Kid Image"
            onChange={onDrop}
            imgExtension={[".jpg", ".gif", ".png", ".gif", "jpeg"]}
            maxFileSize={1048576}
            withPreview={false}
            singleImage={true}
            fileTypeError="File type not supported"
            fileSizeError="File size exceeds 1MB"
            label=""
          />
        ) : (
          <TouchableOpacity onPress={pickImage} style={{ marginBottom: 10 }}>
            <Text style={{ color: '#8dc63f', fontSize: 16, fontWeight: '600' }}>
              Upload Kid Image
            </Text>
          </TouchableOpacity>
        )}
        {form.profilePic && <Image source={{ uri: form.profilePic }} style={styles.previewImage} />}

        {/* Form Fields */}
        <TextInput
          style={styles.input}
          placeholder="Name"
          placeholderTextColor="#999"
          value={form.name}
          onChangeText={(value) => handleChange("name", value)}
        />
        <TextInput
          style={styles.input}
          placeholder="Age"
          placeholderTextColor="#999"
          keyboardType="numeric"
          value={form.age}
          onChangeText={(value) => handleChange("age", value)}
        />
        <View style={styles.pickerContainer}>
          <Picker
            selectedValue={form.gender}
            onValueChange={(value) => handleChange("gender", value)}
            style={styles.picker}
          >
            <Picker.Item label="Select Gender" value="" style={styles.placeholder} />
            <Picker.Item label="Boy" value="BOY" />
            <Picker.Item label="Girl" value="GIRL" />
          </Picker>
          <Image source={Polygon} />
        </View>
        <TextInput
          style={styles.input}
          placeholder="Emergency Number"
          placeholderTextColor="#999"
          keyboardType="phone-pad"
          value={form.emergencyNumber}
          onChangeText={(value) => handleChange("emergencyNumber", value)}
        />
        <TextInput
          style={styles.input}
          placeholder="Any Allergy"
          placeholderTextColor="#999"
          value={form.allergy}
          onChangeText={(value) => handleChange("allergy", value)}
        />
        <TextInput
          style={styles.input}
          placeholder="Any Health Issue"
          placeholderTextColor="#999"
          value={form.healthIssue}
          onChangeText={(value) => handleChange("healthIssue", value)}
        />

        {loading ? (
          <ActivityIndicator size="large" color="#A6CE39" style={styles.loader} />
        ) : (

          <TouchableOpacity style={styles.button} onPress={handleSubmit} disabled={loading}>
            <Text style={styles.buttonText}>Next</Text>
          </TouchableOpacity>
        )}

      </ScrollView>
    </>
  )
}

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    alignItems: "center",
    backgroundColor: "#fff",
    paddingHorizontal: 20,
  },
  previewImage: {
    width: 100,
    height: 100,
    borderRadius: 50,
    marginBottom: 10,
  },
  loader: {
    marginTop: 20,
  },
  input: {
    width: "100%",
    height: 50,
    fontFamily: Platform.OS === "android" ? "FacebookSansRegular" : "Facebook Sans",
    fontStyle: "normal",
    borderColor: "#ccc",
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 10,
    marginBottom: 15,
  },
  pickerContainer: {
    width: "100%",
    height: 50,
    borderColor: "#ccc",
    borderWidth: 1,
    borderRadius: 8,
    fontFamily: Platform.OS === "android" ? "FacebookSansRegular" : "Facebook Sans",
    fontStyle: "normal",
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 15,
    paddingRight: 13,
    backgroundColor: "white",
  },
  picker: {
    flex: 1,
    width: "100%",
    color: "#000",
    borderColor: "white",
    backgroundColor: "white",
  },
  dropdownIcon: {
    width: 10,
    height: 10,
    tintColor: "#999",
  },
  button: {
    width: "100%",
    height: 50,
    backgroundColor: "#a2d45e",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 8,
    marginTop: 20,
  },
  buttonText: {
    color: "#fff",
    fontSize: 16,
    fontFamily: Platform.OS === "android" ? "FacebookSansRegular" : "Facebook Sans",
    fontStyle: "normal",
    fontWeight: "bold",
  },
  placeholder: {
    color: "white",
    borderColor: "white",
    backgroundColor: "white",
  },
})

export default AddKidScreen